/**
 * Google Tag Manager Consent Mode v2 helper.
 *
 * Sets all FOUR required Consent Mode v2 signals based on a ConsentSnapshot:
 *   - analytics_storage    (analytics category)
 *   - ad_storage           (marketing category)
 *   - ad_user_data         (marketing category)
 *   - ad_personalization   (marketing category)
 *
 * See CCF-6: previous implementation only set 2 of 4 signals (analytics_storage + ad_storage),
 * causing GA4 / Google Ads to silently leak ad-related signals without explicit consent.
 *
 * Safe to call in SSR contexts — no-ops if `window.gtag` is unavailable.
 */
import type { ConsentSnapshot } from './types';

type ConsentValue = 'granted' | 'denied';

export interface ConsentModeV2Update {
  analytics_storage: ConsentValue;
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
}

/**
 * Build the Consent Mode v2 payload from a snapshot.
 * Exported separately for testability without touching `window.gtag`.
 */
export function buildConsentModeV2Update(snapshot: ConsentSnapshot): ConsentModeV2Update {
  const analytics: ConsentValue = snapshot.categories.analytics ? 'granted' : 'denied';
  const marketing: ConsentValue = snapshot.categories.marketing ? 'granted' : 'denied';
  return {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  };
}

/**
 * Push a `gtag('consent', 'update', ...)` call with all 4 Consent Mode v2 signals.
 * No-op in environments without `window.gtag` (SSR, tests without GTM stub, etc.).
 */
export function updateGTMConsent(snapshot: ConsentSnapshot): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as {
    gtag?: (...args: unknown[]) => void;
  };
  if (typeof w.gtag !== 'function') return;
  const payload = buildConsentModeV2Update(snapshot);
  w.gtag('consent', 'update', payload);
}
